import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  sendSingleNotificationService,
  sendBulkNotificationService,
  fetchAllNotificationsService,
  sendWhatsAppSingleNotificationService,
  sendWhatsAppBulkNotificationService,
  sendPromoNotificationService,
  createScheduledNotificationService,
  getScheduledNotificationsService,
  getScheduledNotificationByIdService,
  updateScheduledNotificationService,
  deleteScheduledNotificationService,
  toggleScheduledNotificationService,
  sendScheduledNowService,
  dryRunScheduledNotificationService,
} from "../../apis/notifications";
import { cleanObject } from "../../helpers";

interface NotificationState {
  allNotifications: any[];
  loading: boolean;
  error: string | null;
  sendingSingle: boolean;
  sendingBulk: boolean;
  sendingWhatsAppSingle: boolean;
  sendingWhatsAppBulk: boolean;
  sendingPromo: boolean;
  // Scheduled / Automated Messages
  scheduledNotifications: any[];
  scheduledLoading: boolean;
  scheduledCreating: boolean;
  scheduledUpdating: boolean;
  scheduledDeleting: boolean;
  scheduledToggling: number | null;
  scheduledSendingNow: number | null;
  scheduledDryRunning: boolean;
  scheduledDryRunResult: any | null;
}

const initialState: NotificationState = {
  allNotifications: [],
  loading: false,
  error: null,
  sendingSingle: false,
  sendingBulk: false,
  sendingWhatsAppSingle: false,
  sendingWhatsAppBulk: false,
  sendingPromo: false,
  // Scheduled / Automated Messages
  scheduledNotifications: [],
  scheduledLoading: false,
  scheduledCreating: false,
  scheduledUpdating: false,
  scheduledDeleting: false,
  scheduledToggling: null,
  scheduledSendingNow: null,
  scheduledDryRunning: false,
  scheduledDryRunResult: null,
};

// Fetch all notifications
export const fetchAllNotifications = createAsyncThunk(
  "notifications/fetchAll",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetchAllNotificationsService(cleanObject(payload));
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch notifications",
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  },
);

// Send single notification
export const sendSingleNotification = createAsyncThunk(
  "notifications/sendSingle",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendSingleNotificationService(payload);
      toast.success("Single notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send bulk notification
export const sendBulkNotification = createAsyncThunk(
  "notifications/sendBulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendBulkNotificationService(payload);
      toast.success("Bulk notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send WhatsApp single notification
export const sendWhatsAppSingleNotification = createAsyncThunk(
  "notifications/sendWhatsAppSingle",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendWhatsAppSingleNotificationService(payload);
      toast.success("WhatsApp notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send WhatsApp notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send WhatsApp bulk notification
export const sendWhatsAppBulkNotification = createAsyncThunk(
  "notifications/sendWhatsAppBulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendWhatsAppBulkNotificationService(payload);
      toast.success("WhatsApp bulk notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send WhatsApp notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send promo notification to anonymous installs
export const sendPromoNotification = createAsyncThunk(
  "notifications/sendPromo",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendPromoNotificationService(payload);
      const message =
        response?.data?.message ?? "Promo notification sent successfully";

      // Show warning if no installs available, success otherwise
      if (message.toLowerCase().includes("no anonymous installs")) {
        toast.warning(message);
      } else {
        toast.success(message);
      }

      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send promo notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// ── Scheduled / Automated Notification Thunks ──

export const createScheduledNotification = createAsyncThunk(
  "notifications/createScheduled",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await createScheduledNotificationService(payload);
      toast.success("Scheduled notification created successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to create scheduled notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Create failed");
    }
  },
);

export const fetchScheduledNotifications = createAsyncThunk(
  "notifications/fetchScheduled",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getScheduledNotificationsService();
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to fetch scheduled notifications",
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  },
);

export const fetchScheduledNotificationById = createAsyncThunk(
  "notifications/fetchScheduledById",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getScheduledNotificationByIdService(id);
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to fetch scheduled notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  },
);

export const updateScheduledNotification = createAsyncThunk(
  "notifications/updateScheduled",
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await updateScheduledNotificationService(id, data);
      toast.success("Scheduled notification updated successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to update scheduled notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Update failed");
    }
  },
);

export const deleteScheduledNotification = createAsyncThunk(
  "notifications/deleteScheduled",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteScheduledNotificationService(id);
      toast.success("Scheduled notification deleted successfully");
      return id;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to delete scheduled notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Delete failed");
    }
  },
);

export const toggleScheduledNotification = createAsyncThunk(
  "notifications/toggleScheduled",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await toggleScheduledNotificationService(id);
      toast.success("Schedule toggled successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Failed to toggle schedule");
      return rejectWithValue(error.response?.data?.message ?? "Toggle failed");
    }
  },
);

export const sendScheduledNow = createAsyncThunk(
  "notifications/sendScheduledNow",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await sendScheduledNowService(id);
      toast.success("Notification sent immediately");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

export const dryRunScheduledNotification = createAsyncThunk(
  "notifications/dryRunScheduled",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await dryRunScheduledNotificationService(payload);
      toast.success("Dry run completed successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Dry run failed");
      return rejectWithValue(error.response?.data?.message ?? "Dry run failed");
    }
  },
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    resetNotifications: (state) => {
      state.allNotifications = [];
      state.error = null;
    },
    clearDryRunResult: (state) => {
      state.scheduledDryRunResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchAllNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.allNotifications = action.payload;
      })
      .addCase(
        fetchAllNotifications.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        },
      )

      // Send single
      .addCase(sendSingleNotification.pending, (state) => {
        state.sendingSingle = true;
        state.error = null;
      })
      .addCase(sendSingleNotification.fulfilled, (state, action) => {
        state.sendingSingle = false;
      })
      .addCase(
        sendSingleNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingSingle = false;
          state.error = action.payload;
        },
      )

      // Send bulk
      .addCase(sendBulkNotification.pending, (state) => {
        state.sendingBulk = true;
        state.error = null;
      })
      .addCase(sendBulkNotification.fulfilled, (state, action) => {
        state.sendingBulk = false;
      })
      .addCase(
        sendBulkNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingBulk = false;
          state.error = action.payload;
        },
      )

      // Send WhatsApp single
      .addCase(sendWhatsAppSingleNotification.pending, (state) => {
        state.sendingWhatsAppSingle = true;
        state.error = null;
      })
      .addCase(sendWhatsAppSingleNotification.fulfilled, (state, action) => {
        state.sendingWhatsAppSingle = false;
      })
      .addCase(
        sendWhatsAppSingleNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingWhatsAppSingle = false;
          state.error = action.payload;
        },
      )

      // Send WhatsApp bulk
      .addCase(sendWhatsAppBulkNotification.pending, (state) => {
        state.sendingWhatsAppBulk = true;
        state.error = null;
      })
      .addCase(sendWhatsAppBulkNotification.fulfilled, (state, action) => {
        state.sendingWhatsAppBulk = false;
      })
      .addCase(
        sendWhatsAppBulkNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingWhatsAppBulk = false;
          state.error = action.payload;
        },
      )

      // Send Promo
      .addCase(sendPromoNotification.pending, (state) => {
        state.sendingPromo = true;
        state.error = null;
      })
      .addCase(sendPromoNotification.fulfilled, (state, action) => {
        state.sendingPromo = false;
      })
      .addCase(
        sendPromoNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingPromo = false;
          state.error = action.payload;
        },
      )

      // ── Scheduled / Automated Messages ──

      // Create scheduled
      .addCase(createScheduledNotification.pending, (state) => {
        state.scheduledCreating = true;
      })
      .addCase(createScheduledNotification.fulfilled, (state, action) => {
        state.scheduledCreating = false;
        if (action.payload?.data) {
          state.scheduledNotifications = [
            action.payload.data,
            ...state.scheduledNotifications,
          ];
        }
      })
      .addCase(createScheduledNotification.rejected, (state) => {
        state.scheduledCreating = false;
      })

      // Fetch all scheduled
      .addCase(fetchScheduledNotifications.pending, (state) => {
        state.scheduledLoading = true;
      })
      .addCase(fetchScheduledNotifications.fulfilled, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledNotifications =
          action.payload?.data ?? action.payload ?? [];
      })
      .addCase(fetchScheduledNotifications.rejected, (state) => {
        state.scheduledLoading = false;
      })

      // Update scheduled
      .addCase(updateScheduledNotification.pending, (state) => {
        state.scheduledUpdating = true;
      })
      .addCase(updateScheduledNotification.fulfilled, (state, action) => {
        state.scheduledUpdating = false;
        const updated = action.payload?.data ?? action.payload;
        if (updated?.id) {
          state.scheduledNotifications = state.scheduledNotifications.map(
            (n: any) => (n.id === updated.id ? updated : n),
          );
        }
      })
      .addCase(updateScheduledNotification.rejected, (state) => {
        state.scheduledUpdating = false;
      })

      // Delete scheduled (optimistic)
      .addCase(deleteScheduledNotification.pending, (state, action) => {
        state.scheduledDeleting = true;
        // Optimistic: remove from list immediately
        const id = action.meta.arg;
        (state as any)._deletedBackup = state.scheduledNotifications.find(
          (n: any) => n.id === id,
        );
        state.scheduledNotifications = state.scheduledNotifications.filter(
          (n: any) => n.id !== id,
        );
      })
      .addCase(deleteScheduledNotification.fulfilled, (state) => {
        state.scheduledDeleting = false;
        delete (state as any)._deletedBackup;
      })
      .addCase(deleteScheduledNotification.rejected, (state) => {
        state.scheduledDeleting = false;
        // Revert: add back on failure
        const backup = (state as any)._deletedBackup;
        if (backup) {
          state.scheduledNotifications.push(backup);
        }
        delete (state as any)._deletedBackup;
      })

      // Toggle scheduled (optimistic)
      .addCase(toggleScheduledNotification.pending, (state, action) => {
        state.scheduledToggling = action.meta.arg;
        // Optimistic: flip isActive immediately
        state.scheduledNotifications = state.scheduledNotifications.map(
          (n: any) =>
            n.id === action.meta.arg ? { ...n, isActive: !n.isActive } : n,
        );
      })
      .addCase(toggleScheduledNotification.fulfilled, (state, action) => {
        state.scheduledToggling = null;
        // Reconcile with server response
        const toggled = action.payload?.data ?? action.payload;
        if (toggled?.id) {
          state.scheduledNotifications = state.scheduledNotifications.map(
            (n: any) => (n.id === toggled.id ? toggled : n),
          );
        }
      })
      .addCase(toggleScheduledNotification.rejected, (state, action) => {
        state.scheduledToggling = null;
        // Revert: flip back on failure
        const id = action.meta.arg;
        state.scheduledNotifications = state.scheduledNotifications.map(
          (n: any) => (n.id === id ? { ...n, isActive: !n.isActive } : n),
        );
      })

      // Send now
      .addCase(sendScheduledNow.pending, (state, action) => {
        state.scheduledSendingNow = action.meta.arg;
      })
      .addCase(sendScheduledNow.fulfilled, (state) => {
        state.scheduledSendingNow = null;
      })
      .addCase(sendScheduledNow.rejected, (state) => {
        state.scheduledSendingNow = null;
      })

      // Dry run
      .addCase(dryRunScheduledNotification.pending, (state) => {
        state.scheduledDryRunning = true;
        state.scheduledDryRunResult = null;
      })
      .addCase(dryRunScheduledNotification.fulfilled, (state, action) => {
        state.scheduledDryRunning = false;
        state.scheduledDryRunResult = action.payload;
      })
      .addCase(dryRunScheduledNotification.rejected, (state) => {
        state.scheduledDryRunning = false;
        state.scheduledDryRunResult = null;
      });
  },
});

export const { resetNotifications, clearDryRunResult } =
  notificationSlice.actions;
export default notificationSlice.reducer;
